"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react"
import {
  facebookPagePluginSrc,
  type SocialEmbedSlide,
} from "@/lib/social-embeds"

declare global {
  interface Window {
    instgrm?: { Embeds: { process: () => void } }
    twttr?: { widgets: { load: (el?: HTMLElement) => void } }
  }
}

const INSTAGRAM_SCRIPT = "https://www.instagram.com/embed.js"
const TWITTER_SCRIPT = "https://platform.twitter.com/widgets.js"

function loadScriptOnce(src: string, id: string): Promise<void> {
  return new Promise((resolve) => {
    if (document.getElementById(id)) {
      resolve()
      return
    }
    const script = document.createElement("script")
    script.id = id
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => resolve()
    document.body.appendChild(script)
  })
}

function PlatformBadge({ slide }: { slide: SocialEmbedSlide }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-3">
      <p className="text-sm font-medium text-foreground">{slide.label}</p>
      <a
        href={slide.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        Open
        <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  )
}

function InstagramSlide({ slide, active }: { slide: SocialEmbedSlide; active: boolean }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!active) return
    loadScriptOnce(INSTAGRAM_SCRIPT, "instagram-embed-js").then(() => {
      window.instgrm?.Embeds.process()
    })
  }, [active, slide.url])

  if (!active) {
    return <div className="h-[520px] rounded-2xl bg-muted/40 animate-pulse" aria-hidden />
  }

  return (
    <div ref={ref} className="min-h-[520px] flex items-start justify-center overflow-hidden rounded-2xl bg-white">
      <blockquote
        className="instagram-media"
        data-instgrm-permalink={slide.url}
        data-instgrm-version="14"
        style={{
          background: "#FFF",
          border: 0,
          borderRadius: "12px",
          boxShadow: "none",
          margin: 0,
          maxWidth: "540px",
          minWidth: "280px",
          padding: 0,
          width: "100%",
        }}
      />
    </div>
  )
}

function TikTokSlide({ slide, active }: { slide: SocialEmbedSlide; active: boolean }) {
  if (!active || !slide.iframeSrc) {
    return <div className="h-[620px] rounded-2xl bg-muted/40 animate-pulse" aria-hidden />
  }

  return (
    <div className="overflow-hidden rounded-2xl bg-black">
      <iframe
        src={slide.iframeSrc}
        title={`${slide.label} feed`}
        className="w-full border-0"
        style={{ height: "620px", minHeight: "520px" }}
        allow="encrypted-media; autoplay; clipboard-write"
        loading="lazy"
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      />
    </div>
  )
}

function TwitterSlide({ slide, active }: { slide: SocialEmbedSlide; active: boolean }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!active || !ref.current) return
    loadScriptOnce(TWITTER_SCRIPT, "twitter-widgets-js").then(() => {
      window.twttr?.widgets.load(ref.current ?? undefined)
    })
  }, [active, slide.url])

  if (!active) {
    return <div className="h-[560px] rounded-2xl bg-muted/40 animate-pulse" aria-hidden />
  }

  return (
    <div ref={ref} className="min-h-[560px] overflow-hidden rounded-2xl bg-white px-1">
      <a
        className="twitter-timeline"
        data-height="560"
        data-theme="light"
        data-chrome="nofooter noborders transparent"
        href={slide.url}
      >
        Posts on {slide.label}
      </a>
    </div>
  )
}

function FacebookSlide({ slide, active }: { slide: SocialEmbedSlide; active: boolean }) {
  if (!active) {
    return <div className="h-[620px] rounded-2xl bg-muted/40 animate-pulse" aria-hidden />
  }

  return (
    <div className="overflow-hidden rounded-2xl bg-white">
      <iframe
        src={facebookPagePluginSrc(slide.url, 500)}
        title={`${slide.label} feed`}
        className="w-full border-0"
        style={{ height: "620px", minHeight: "520px" }}
        loading="lazy"
        allow="encrypted-media"
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      />
    </div>
  )
}

function YouTubeSlide({ slide }: { slide: SocialEmbedSlide }) {
  return (
    <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 rounded-2xl border border-border bg-muted/30 px-6 py-10 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600 text-white">
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7" aria-hidden="true">
          <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33ZM9.75 15.02V8.48l5.75 3.27-5.75 3.27Z" />
        </svg>
      </div>
      <div>
        <p className="text-sm font-medium text-foreground">Latest on YouTube</p>
        <p className="mt-1 text-xs text-muted-foreground max-w-xs">
          Clips and uploads open on YouTube — profile feeds can&apos;t play inline here yet.
        </p>
      </div>
      <a
        href={slide.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-xs font-medium text-background hover:opacity-90 transition-opacity"
      >
        Watch on YouTube
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </div>
  )
}

function EmbedSlideBody({ slide, active }: { slide: SocialEmbedSlide; active: boolean }) {
  switch (slide.kind) {
    case "instagram":
      return <InstagramSlide slide={slide} active={active} />
    case "tiktok-iframe":
      return <TikTokSlide slide={slide} active={active} />
    case "twitter-timeline":
      return <TwitterSlide slide={slide} active={active} />
    case "facebook-page":
      return <FacebookSlide slide={slide} active={active} />
    case "youtube-channel":
      return <YouTubeSlide slide={slide} />
    default:
      return null
  }
}

export function BarberSocialEmbedRail({ slides }: { slides: SocialEmbedSlide[] }) {
  const railRef = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)

  const slideStep = useCallback(() => {
    const el = railRef.current
    return el && el.clientWidth > 0 ? el.clientWidth : 0
  }, [])

  const measureScroll = useCallback(() => {
    const el = railRef.current
    if (!el) return
    const step = slideStep()
    if (step <= 0) return
    const next = Math.min(slides.length - 1, Math.max(0, Math.round(el.scrollLeft / step)))
    setIndex((prev) => (prev === next ? prev : next))
  }, [slideStep, slides.length])

  const goTo = useCallback(
    (i: number) => {
      const el = railRef.current
      if (!el) return
      const step = slideStep()
      if (step <= 0) return
      const clamped = Math.min(slides.length - 1, Math.max(0, i))
      el.scrollTo({ left: clamped * step, behavior: "smooth" })
      setIndex(clamped)
    },
    [slideStep, slides.length],
  )

  useEffect(() => {
    measureScroll()
    window.addEventListener("resize", measureScroll)
    return () => window.removeEventListener("resize", measureScroll)
  }, [measureScroll])

  if (slides.length === 0) return null

  return (
    <section aria-label="Social feeds" className="pt-2">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-foreground">Social</h3>
        {slides.length > 1 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => goTo(index - 1)}
              disabled={index === 0}
              className="rounded-full p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
              aria-label="Previous social feed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => goTo(index + 1)}
              disabled={index === slides.length - 1}
              className="rounded-full p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
              aria-label="Next social feed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="relative rounded-2xl border border-border bg-card overflow-hidden">
        <div
          ref={railRef}
          onScroll={measureScroll}
          className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {slides.map((slide, i) => (
            <div
              key={slide.id}
              className="w-full shrink-0 snap-center snap-always px-4 py-4 sm:px-5 sm:py-5"
              aria-hidden={i !== index}
            >
              <PlatformBadge slide={slide} />
              <EmbedSlideBody slide={slide} active={i === index} />
            </div>
          ))}
        </div>

        {slides.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 border-t border-border px-4 py-3">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => goTo(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? "w-5 bg-foreground" : "w-1.5 bg-muted-foreground/35 hover:bg-muted-foreground/55"
                }`}
                aria-label={`Show ${slide.label} feed`}
                aria-current={i === index ? "true" : undefined}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
